import _get from 'lodash/get';
import MonitoringFilterFieldNames from '@lib/enums/fieldNames/monitoringFilterFieldNames';
import { Values } from '@lib/interfaces/form';
import { FeFilterPreset } from 'graphql-common';
import {
  MonitoringEntity,
  MonitoringType,
  StatusKind,
} from 'routes/Dashboard/Monitoring/enums';
import getFilterFieldNameByMonitoringEntity from './getFilterFieldNameByMonitoringEntity';
import getLegendFilterName from './getLegendFilterName';

// Normalize `period` value: server may return either [from, to] or { from, to }
function normalizePeriodValue(value: unknown): string[] | undefined {
  if (Array.isArray(value)) return value as string[];
  const from = _get(value, 'from');
  const to = _get(value, 'to');
  if (from && to) return [from, to] as string[];
  return undefined;
}

// Build a filter-params patch from a FeFilterPreset
export default function applyFilterPresetToFilters({
  preset,
  monitoringEntity,
  statusKind,
}: {
  preset: FeFilterPreset;
  monitoringEntity: MonitoringEntity;
  statusKind?: StatusKind;
}): Values {
  const patch: Values = {
    [MonitoringFilterFieldNames.LastUpdate]: new Date().toISOString(),
    [getFilterFieldNameByMonitoringEntity(
      monitoringEntity,
      MonitoringFilterFieldNames.FilterPresetId,
    )]: preset.id,
  };

  // 1) Data payload (as saved by createFilterPresetVariables)
  const data = _get(preset, 'data');
  if (data && typeof data === 'object') {
    // Status value
    const statusKey = getFilterFieldNameByMonitoringEntity(
      monitoringEntity,
      MonitoringFilterFieldNames.Status,
    );
    const hasFiltersPresetFlag = Object.prototype.hasOwnProperty.call(
      data as any,
      'filtersPreset',
    );
    const filtersPresetEnabled = Boolean((data as any).filtersPreset);
    // Backward-compatibility: if filtersPreset flag is absent, apply status when present.
    // If flag is present, apply status only when filters were part of the preset.
    if (statusKey in (data as any)) {
      if (!hasFiltersPresetFlag || filtersPresetEnabled) {
        patch[statusKey] = (data as any)[statusKey];
      }
    }

    // Period value (normalize shape)
    const periodKey = getFilterFieldNameByMonitoringEntity(
      monitoringEntity,
      MonitoringFilterFieldNames.Period,
    );
    if (periodKey in (data as any)) {
      const normalized = normalizePeriodValue((data as any)[periodKey]);
      if (normalized) patch[periodKey] = normalized;
    }

    // Map legends:
    // - Performance: stored under generic key -> copy as-is
    const perfLegendKey = getLegendFilterName(
      monitoringEntity,
      MonitoringType.Performance,
    );
    if (perfLegendKey in (data as any)) {
      patch[perfLegendKey] = (data as any)[perfLegendKey];
    }
    // - Anomaly: stored under generic key -> materialize into status-specific key
    const anomalyGenericKey = getLegendFilterName(
      monitoringEntity,
      MonitoringType.Anomaly,
    );
    if (anomalyGenericKey in (data as any) && statusKind !== undefined) {
      const anomalyKeyForStatus = getLegendFilterName(
        monitoringEntity,
        MonitoringType.Anomaly,
        statusKind,
      );
      patch[anomalyKeyForStatus] = (data as any)[anomalyGenericKey];
    }
  }

  // 2) Entities payload (ids only) -> convert to Option[] with value-only
  const entities = _get(preset, 'entities');
  if (entities && typeof entities === 'object') {
    // Ranges
    const sites = _get(entities, 'sites');
    if (Array.isArray(sites) && sites.length) {
      patch[MonitoringFilterFieldNames.Sites] = sites.map((it) => ({
        value: it.id,
      }));
    }
    const sectors = _get(entities, 'sectors');
    if (Array.isArray(sectors) && sectors.length) {
      patch[MonitoringFilterFieldNames.Sectors] = sectors.map((it) => ({
        value: it.id,
      }));
    }

    // Filters: categories and templates are namespaced per entity
    const categories = _get(entities, 'formCategories');
    const keyFormCategory = getFilterFieldNameByMonitoringEntity(
      monitoringEntity,
      MonitoringFilterFieldNames.FormCategory,
    );
    const keyFormTemplateName = getFilterFieldNameByMonitoringEntity(
      monitoringEntity,
      MonitoringFilterFieldNames.FormTemplateName,
    );
    if (Array.isArray(categories)) {
      patch[keyFormCategory] = categories.map((it) => ({ value: it.id }));
    } else if ((data as any)?.filtersPreset) {
      patch[keyFormCategory] = [];
    }
    const templates = _get(entities, 'formTemplates');
    if (Array.isArray(templates)) {
      patch[keyFormTemplateName] = templates.map((it) => ({ value: it.id }));
    } else if ((data as any)?.filtersPreset) {
      patch[keyFormTemplateName] = [];
    }
  }

  return patch;
}
